import { useEffect, useState } from "react";
import { BadgeCheck, CalendarDays, Phone, ShoppingBag, Star, Trash2, X } from "lucide-react";
import {
  clubErrorMessage,
  deleteClubCustomer,
  getClubCustomer,
  updateClubCustomer,
} from "../../api/club";
import BirthdayField, { isBirthdayComplete } from "./BirthdayField";
import {
  SEGMENT_HINTS,
  SOURCE_LABELS,
  formatDate,
  formatDateTime,
  formatLastVisit,
  formatPhone,
  formatToman,
  segmentOf,
  toFa,
} from "./clubFormat";

const RESERVATION_STATUS_LABELS = {
  PENDING: "در انتظار تأیید",
  CONFIRMED: "تأیید شده",
  SEATED: "حاضر در سالن",
  COMPLETED: "انجام شده",
  CANCELLED: "لغو شده",
  REJECTED: "رد شده",
  NO_SHOW: "نیامد",
};
const ORDER_STATUS_LABELS = {
  pending: "جدید",
  processing: "در حال آماده‌سازی",
  completed: "تکمیل شده",
  canceled: "لغو شده",
};
const REVIEW_STATUS_LABELS = { pending: "در انتظار بررسی", approved: "تأیید شده", rejected: "رد شده" };

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10";

const toForm = (customer) => ({
  name: customer.name || "",
  birthday: customer.birthday || "",
  tagsText: (customer.tags || []).join("، "),
  notes: customer.notes || "",
  marketingConsent: Boolean(customer.marketingConsent),
});

const Stat = ({ label, value }) => (
  <div className="bg-white px-3 py-2.5">
    <p className="text-[11px] text-gray-500">{label}</p>
    <p className="mt-0.5 truncate text-sm font-extrabold text-accent">{value}</p>
  </div>
);

const TimelineItem = ({ event }) => {
  if (event.type === "reservation") {
    return (
      <li className="flex gap-3 py-2.5">
        <CalendarDays size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-gray-800">رزرو برای {toFa(event.partySize)} نفر</p>
          <p className="text-xs text-gray-500">
            {formatDateTime(event.at)} · {RESERVATION_STATUS_LABELS[event.status] || event.status}
          </p>
        </div>
      </li>
    );
  }
  if (event.type === "order") {
    return (
      <li className="flex gap-3 py-2.5">
        <ShoppingBag size={16} className="mt-0.5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold text-gray-800">
            سفارش #{toFa(event.orderNumber)} · {formatToman(event.totalPrice)}
          </p>
          <p className="text-xs text-gray-500">
            {formatDateTime(event.at)} · {toFa(event.itemCount)} قلم · {ORDER_STATUS_LABELS[event.status] || event.status}
          </p>
        </div>
      </li>
    );
  }
  return (
    <li className="flex gap-3 py-2.5">
      <Star size={16} className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-gray-800">نظر {toFa(event.stars)} ستاره</p>
        {event.comment && <p className="mt-0.5 line-clamp-2 text-xs leading-5 text-gray-600">{event.comment}</p>}
        <p className="text-xs text-gray-500">
          {formatDate(event.at)} · {REVIEW_STATUS_LABELS[event.status] || event.status}
        </p>
      </div>
    </li>
  );
};

export default function CustomerDetailPanel({ restaurantId, customerId, onClose, onUpdated, onDeleted }) {
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedNotice, setSavedNotice] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getClubCustomer(restaurantId, customerId)
      .then((data) => {
        if (!active) return;
        setDetail(data);
        setForm(toForm(data.customer));
      })
      .catch((loadError) => active && setError(clubErrorMessage(loadError, "دریافت اطلاعات مشتری ناموفق بود.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [restaurantId, customerId]);

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (key) => (value) => {
    setSavedNotice(false);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    if (!isBirthdayComplete(form.birthday)) return setError("روز و ماه تولد را کامل انتخاب کنید.");
    setSaving(true);
    setError("");
    try {
      const data = await updateClubCustomer(restaurantId, customerId, {
        name: form.name,
        birthday: form.birthday,
        notes: form.notes,
        marketingConsent: form.marketingConsent,
        tags: form.tagsText.split(/[,،]/).map((tag) => tag.trim()).filter(Boolean),
      });
      setDetail(data);
      setForm(toForm(data.customer));
      setSavedNotice(true);
      onUpdated(data.customer);
    } catch (saveError) {
      setError(clubErrorMessage(saveError, "ذخیره تغییرات ناموفق بود."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("این مشتری از باشگاه حذف شود؟ سوابق رزرو و سفارش او حذف نمی‌شود.")) return;
    try {
      await deleteClubCustomer(restaurantId, customerId);
      onDeleted(customerId);
    } catch (deleteError) {
      setError(clubErrorMessage(deleteError, "حذف مشتری ناموفق بود."));
    }
  };

  const customer = detail?.customer;
  const segment = customer ? segmentOf(customer.segment) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/30" dir="rtl" role="presentation" onMouseDown={onClose}>
      <aside
        onMouseDown={(event) => event.stopPropagation()}
        className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-gray-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="customer-detail-title"
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4">
          {customer ? (
            <div className="min-w-0">
              <h2 id="customer-detail-title" className="truncate text-base font-extrabold text-accent">
                {customer.name || "بدون نام"}
              </h2>
              <a href={`tel:${customer.phone}`} className="mt-1 inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary" dir="ltr">
                <Phone size={13} aria-hidden="true" />
                {formatPhone(customer.phone)}
              </a>
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className={`font-bold ${segment.className}`} title={SEGMENT_HINTS[customer.segment]}>
                  {segment.label}
                </span>
                <span className="text-gray-300">·</span>
                <span className="text-gray-500">{SOURCE_LABELS[customer.source] || customer.source}</span>
                {customer.phoneVerified && (
                  <>
                    <span className="text-gray-300">·</span>
                    <span className="inline-flex items-center gap-1 text-secondary">
                      <BadgeCheck size={13} aria-hidden="true" /> تأیید شده
                    </span>
                  </>
                )}
              </p>
            </div>
          ) : (
            <h2 id="customer-detail-title" className="text-base font-extrabold text-accent">پروفایل مشتری</h2>
          )}
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="بستن">
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto p-5">
          {loading && (
            <div className="space-y-3" aria-busy="true">
              <div className="h-16 animate-pulse rounded-xl bg-gray-200" />
              <div className="h-40 animate-pulse rounded-xl bg-gray-200" />
            </div>
          )}

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-700" role="alert">{error}</p>}

          {customer && form && (
            <>
              <section className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-gray-200 bg-gray-200" aria-label="خلاصه مشتری">
                <Stat label="تعداد مراجعه" value={toFa(customer.stats.visitCount)} />
                <Stat label="آخرین مراجعه" value={formatLastVisit(customer.stats.lastVisitAt)} />
                <Stat label="مجموع خرید" value={formatToman(customer.stats.totalSpent)} />
                <Stat label="عضو از" value={formatDate(customer.createdAt)} />
              </section>

              <form onSubmit={save} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-extrabold text-accent">اطلاعات</h3>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">نام</span>
                  <input value={form.name} onChange={(e) => set("name")(e.target.value)} maxLength={120} className={`${inputClass} h-10`} />
                </label>
                <div>
                  <span className="mb-1 block text-xs font-semibold text-gray-600">تاریخ تولد</span>
                  <BirthdayField value={form.birthday} onChange={set("birthday")} idPrefix="edit-birthday" />
                </div>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">برچسب‌ها</span>
                  <input
                    value={form.tagsText}
                    onChange={(e) => set("tagsText")(e.target.value)}
                    placeholder="مثلاً: گیاهخوار، میز کنار پنجره"
                    className={`${inputClass} h-10`}
                  />
                  <span className="mt-1 block text-[11px] text-gray-400">با ویرگول جدا کنید.</span>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">یادداشت</span>
                  <textarea
                    value={form.notes}
                    onChange={(e) => set("notes")(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    className={`${inputClass} py-2 leading-6`}
                  />
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.marketingConsent}
                    onChange={(e) => set("marketingConsent")(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-primary"
                  />
                  <span>با دریافت پیامک‌های تبلیغاتی موافق است</span>
                </label>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex min-h-9 items-center rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-[#e8731f] disabled:opacity-60"
                  >
                    {saving ? "در حال ذخیره…" : "ذخیره تغییرات"}
                  </button>
                  {savedNotice && <span className="text-xs font-semibold text-secondary">ذخیره شد</span>}
                </div>
              </form>

              <section className="rounded-xl border border-gray-200 bg-white px-4 pt-3">
                <h3 className="text-sm font-extrabold text-accent">سابقه</h3>
                {detail.timeline.length ? (
                  <ul className="divide-y divide-gray-100">
                    {detail.timeline.map((event) => <TimelineItem key={event.id} event={event} />)}
                  </ul>
                ) : (
                  <p className="pb-4 pt-2 text-xs text-gray-500">هنوز رزرو، سفارش یا نظری از این مشتری ثبت نشده است.</p>
                )}
              </section>

              <button type="button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:underline">
                <Trash2 size={14} aria-hidden="true" />
                حذف از باشگاه
              </button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
